import {ReactNode} from 'react';

import {ProductGroup} from 'core/constants/productGroups';
import {AuthenticDetectionContext, AuthenticStatus, ProductsStatus} from 'core/dictionaries';

import {ApiFilters, ApiPaging, ApiSortings} from './api';
import {AggregationStorageLocation} from './documents';
import {PackageTypes} from './gtins';

export interface ProductCommonRaw {
    parentMarkingCode: string | null;
    markingCode: string;
    packagingLevel: number;
    fileLink: string;
    status: ProductsStatus;
    product: {
        gtin: string;
        productTrademark: string;
        manufacturerName: string;
        productName: string;
        batchNumber: string;
        manufacturingDate: string;
        productGroupCode: ProductGroup;
    };
    statuses: {
        [key: string]: {
            status: string;
            operationTime: string;
            parentMarkingCode: string;
        };
    };
    packagingType: PackageTypes;
    childrenTotalCount: number;
    owner: string | null;
    storageLocation: AggregationStorageLocation | null;
    markingCodeAuthenticInfo: Partial<{
        authenticStatus: AuthenticStatus;
        authenticDetectionContext: AuthenticDetectionContext;
        authenticDetectionDateTime: string;
        authenticReason: string;
        documentId: string;
    }> | null;
}

export interface ProductRaw extends ProductCommonRaw {
    operations: ProductOperationsRawRow[];
}

export interface ProductFormatted {
    name: string;
    code: string;
    status: string;
    brand: string;
    gtin: string;
    generatedTime: string;
    issuedTime: string;
    appliedTime: string;
    aggregationInfo: ReactNode;
    enteredCirculationTime: string;
    leftCirculationTime: string;
    fileLink: string;
    packagingLevel: number;
    isAggregate: boolean;
    isGroup: boolean;
    storageLocation: string;
    storageName: string;
    packagingType?: string;
    productGroupCode?: ReactNode;
    authentic?: {
        status: ReactNode;
        date: string;
        type: string;
        link: ReactNode | string;
    };
    batchNumber?: string;
    isTransportPackage?: boolean;
    manufacturingDate?: string;
}

export interface ProductOperationsRawRow {
    operationAction: ProductsStatus;
    operationTime: string;
    orderId: string | null;
    reportId: string | null;
    clientName: string;
    source: 'CUSTOMS' | string | null;
    storageLocationId: number | null;
    storageLocationName: string | null;
}

export enum ProductOperationsAccessors {
    documentType = 'operationAction',
    date = 'operationTime',
    orderId = 'orderId',
    documentId = 'reportId',
    clientName = 'clientName',
    storageLocation = 'storageLocationId',
}

export interface ProductTracing {
    costVatFree: string;
    costWithVat: string;
    gtin: string;
    id: string;
    inn: string;
    markingCode: string;
    name: string;
    price: string;
    quantity: string;
    updDate: string;
}

export interface ProductOperationsFormattedRow {
    [ProductOperationsAccessors.documentType]: string;
    [ProductOperationsAccessors.orderId]?: string;
    [ProductOperationsAccessors.date]: string;
    [ProductOperationsAccessors.clientName]: string;
    [ProductOperationsAccessors.documentId]: ReactNode;
    [ProductOperationsAccessors.storageLocation]: string;
}

export interface ProductOperationFilter {
    operations?: ProductsStatus[];
    changeDateFrom?: string;
    changeDateTo?: string;
    documentNumber?: string;
    documentSender?: string;
    storageLocationId?: number;
}

export interface ProductContentRawRow {
    gtin: string;
    productName: string;
    markingCode: string;
    status: ProductsStatus;
    modifiedAt: string;
    packagingType: PackageTypes;
    manufacturerName: string;
    productGroupCode: ProductGroup;
    authenticStatus: AuthenticStatus;
    parentMarkingCode: string | null;
    parentGtin: string | null;
    owner: string | null;
    storageLocationAddress: string | null;
}

export interface ProductContentRawResponse {
    nextPageRef: string;
    data: ProductContentRawRow[];
}

export interface ProductContentRawRequest {
    reportId: string;
    paging: ApiPaging | {pageRef: string | null};
    sortings: ApiSortings;
    filters: ApiFilters;
    pageRef?: string | null;
}

export enum ProductContentAccessors {
    name = 'productName',
    code = 'markingCode',
    status = 'status',
    modifiedDate = 'modifiedAt',
    packageType = 'packagingType',
    manufacturer = 'manufacturerName',
    productGroupCode = 'productGroupCode',
    owner = 'owner',
    storageLocationAddress = 'storageLocationAddress',
    authenticStatus = 'authenticStatus',
    parentMarkingCode = 'parentMarkingCode',
}

export interface ProductContentFormattedRow {
    [ProductContentAccessors.name]: string;
    [ProductContentAccessors.code]: ReactNode;
    [ProductContentAccessors.status]: string;
    [ProductContentAccessors.modifiedDate]: string;
    [ProductContentAccessors.packageType]: string;
    [ProductContentAccessors.manufacturer]: string;
    [ProductContentAccessors.owner]?: string;
    [ProductContentAccessors.storageLocationAddress]?: string;
    [ProductContentAccessors.authenticStatus]?: string;
    [ProductContentAccessors.parentMarkingCode]?: ReactNode | string;
}
